import React, { useEffect, useState, useRef, useCallback } from 'react';
import useZoom from '../../hooks/useZoom';
import { getZoomLevel } from '../../utils/zoom';
import { resolveRefToElement } from '../../utils/refResolver';
import styles from './styles.module.scss';

export default function UIHelperBackdrop({ helper, onClickOutside }) {
  const { getCorrectedRect, onZoomChange } = useZoom();
  const [targetRect, setTargetRect] = useState(null);
  const backdropRef = useRef();

  const updateTargetRect = useCallback(() => {
    const element = resolveRefToElement(helper?.ref);
    if (!element) return;

    const correctedRect = getCorrectedRect(element);
    setTargetRect(correctedRect);
  }, [helper, getCorrectedRect]);

  useEffect(() => {
    updateTargetRect();

    // Temporarily increase target element z-index to appear above backdrop
    const element = resolveRefToElement(helper?.ref);
    let originalZIndex = '';
    let originalInlinePosition = '';
    let cleanupClass = () => {};
    if (element) {
      originalZIndex = element.style.zIndex || '';
      originalInlinePosition = element.style.position || '';
      const computedPosition = window.getComputedStyle(element).position;
      if (computedPosition === 'static') {
        element.style.position = 'relative';
      }
      element.style.zIndex = '100001'; // Above backdrop, below overlay
      element.classList.add(styles.uiHelperTargetPulse);
      cleanupClass = () => element.classList.remove(styles.uiHelperTargetPulse);
    }

    // Update position when zoom changes
    const cleanup = onZoomChange(() => {
      setTimeout(updateTargetRect, 0);
    });

    return () => {
      cleanup();
      // Restore original z-index/position
      if (element) {
        element.style.zIndex = originalZIndex;
        element.style.position = originalInlinePosition;
        cleanupClass();
      }
    };
  }, [updateTargetRect, onZoomChange, helper]);

  const handleBackdropClick = (event) => {
    if (event.target === backdropRef.current) {
      onClickOutside?.();
    }
  };


  const { backdrop = 'blur' } = helper;
  const backdropClass = styles[`uiHelperBackdrop${backdrop.charAt(0).toUpperCase() + backdrop.slice(1)}`] || styles.uiHelperBackdropBlur;

  if (!targetRect) {
    // Fallback: simple backdrop without hole
    return (
      <div
        ref={backdropRef}
        className={`${styles.uiHelperBackdrop} ${backdropClass}`}
        onClick={handleBackdropClick}
      />
    );
  }

  // Spotlight backdrop: four segments around the target rect, leaving the target visible
  const { top, left, width, height } = targetRect;
  const zoom = getZoomLevel() || 1;
  const viewportHeight = window.innerHeight / zoom;
  const bottom = viewportHeight - (top + height);
  const haloColor = (() => {
    switch (backdrop) {
      case 'darken':
        return 'rgba(0, 0, 0, 0.6)';
      case 'spotlight':
        return 'rgba(0, 0, 0, 0.55)';
      case 'none':
        return 'transparent';
      default: // blur and others
        return 'rgba(0, 0, 0, 0.45)';
    }
  })();

  return (
    <div ref={backdropRef} className={styles.uiHelperBackdrop} style={{ pointerEvents: 'auto' }} onClick={handleBackdropClick}>
      <div
        className={backdropClass}
        style={{ top: 0, left: 0, right: 0, height: top, position: 'absolute' }}
      />
      <div
        className={backdropClass}
        style={{ top, left: 0, width: left, height, position: 'absolute' }}
      />
      <div
        className={backdropClass}
        style={{ top, left: left + width, right: 0, height, position: 'absolute' }}
      />
      <div
        className={backdropClass}
        style={{ top: top + height, left: 0, right: 0, height: bottom, position: 'absolute' }}
      />
      <div
        className={styles.uiHelperSpotlightHalo}
        style={{
          top,
          left,
          width,
          height,
          '--ui-helper-halo': haloColor,
        }}
      />
    </div>
  );
}
